<script setup lang="ts">import { computed as _computed } from 'vue';

import type { Suggestion } from '@consumer/logic/v2/header'
import { isMobile } from '@consumer/utils/breakpoints'
import MultiPlaceGiftIdeasApi from '@consumer/api/MultiPlaceGiftIdeasApi'
import type MultiplaceGift from '@consumer/types/serializers/MultiplaceGift'
import type PlaceDetail from '@consumer/types/PlaceDetail'
import { createReusableTemplate } from '@vueuse/core'
import { pluralize } from '@corp/helpers/string'
import closeIcon from '@consumer/assets/icons/close.svg?svgicon'
import everywhereIcon from '@consumer/assets/icons/everywhere.svg?svgicon'
import newMapPinIcon from '@consumer/assets/icons/new/map-pin.svg?svgicon'
import SuggestionCity from '../v2/layout/Header/SearchBar/SuggestionCity.vue'
import PlaceMap from './PlaceMap.vue'

withDefaults(defineProps<{
  multiplaceGift: MultiplaceGift
}>(), {  })

const inputModel = defineModel<string>()
const hasResults = ref<boolean>(!inputModel.value?.length)
// const openSuggestions = ref<boolean>(false)
const suggestionComponentRef = ref<InstanceType<typeof SuggestionCity>>()
const selectedSuggestion = ref<Suggestion>()
const suggestedPlaces = ref<PlaceDetail[]>([])
const processingSuggestion = ref<boolean>(false)
const noResults = ref<string | null>()
const [DefineCityDropdown, ReuseCityDropdown] = createReusableTemplate()
const selectSuggestion = (suggestion: Suggestion) => {
  selectedSuggestion.value = suggestion
}

const popperCloseButton = ref()
const numberOfNationwidePlaces = computed(() => __props.multiplaceGift.places?.length ?? 0)

const showCityDropdown = computed(() => {
  return (inputModel.value?.length ?? 0) > 0 && hasResults.value
})
const suggestedForUseCopy = _computed(() => {
  if (numberOfNationwidePlaces.value === 1) {
    return 'Suggested for use at 1 business!'
  }
  else {
    return `Suggested for use at ${numberOfNationwidePlaces.value} brands and businesses!`
  }
})

const onInput = () => {
  suggestionComponentRef.value?.highlightFirstSuggestion()
}

function highlightNextSuggestion (evt: KeyboardEvent) {
  evt.stopImmediatePropagation()
  suggestionComponentRef.value?.highlightNextSuggestion()
}

function highlightPreviousSuggestion (evt: KeyboardEvent) {
  evt.stopImmediatePropagation()
  suggestionComponentRef.value?.highlightPreviousSuggestion()
}

function selectHighlightedSuggestion (evt: KeyboardEvent) {
  evt.stopImmediatePropagation()
  suggestionComponentRef.value?.selectHighlightedSuggestion()
}

async function apiCall (data: { city: string | undefined; stateCode: string | undefined }) {
  if (__props.multiplaceGift.status === 'draft') {
    return MultiPlaceGiftIdeasApi.nationwidePlaceLocationsForCity({ params: __props.multiplaceGift, data })
  }
  else if (__props.multiplaceGift.createdBy?.username != null) {
    return MultiPlaceGiftIdeasApi.multiplaceGiftWithUsernameNationwidePlaceLocationsForCity({
      params: {
        username: __props.multiplaceGift.createdBy.username,
        slug: __props.multiplaceGift.slug,
      },
      data,
    })
  }
  else {
    return MultiPlaceGiftIdeasApi.multiplaceGiftGiftlyNationwidePlaceLocationsForCity({ params: __props.multiplaceGift, data })
  }
}

watch(selectedSuggestion, async () => {
  if (selectedSuggestion.value) {
    processingSuggestion.value = true
    inputModel.value = ''
    try {
      const resp = await apiCall({
        city: selectedSuggestion.value.city,
        stateCode: selectedSuggestion.value.stateCode,
      })

      suggestedPlaces.value.splice(0, suggestedPlaces.value.length, ...resp)
      if (suggestedPlaces.value.length === 0) {
        noResults.value = `No results for ${selectedSuggestion.value.city}, ${selectedSuggestion.value.stateCode}`
      }
      else {
        popperCloseButton.value?.click()
        noResults.value = null
      }
    }
    finally {
      processingSuggestion.value = false
    }
  }
})
</script>
<template>
  <DefineCityDropdown>
    <div class="w-[470px] p-6 flex flex-col gap-[20px] h-full">
      <div class="flex flex-row items-center">
        <div class="text-lg font-weight-600 flex-grow">Enter the recipient's location</div>
        <GIcon
          ref="popperCloseButton"
          v-close-popper
          :svg="closeIcon"
          color="black"
          size="16px"
          class="clickable"
        />
      </div>
      <div class="flex-grow lg:hidden"/>
      <div class="flex flex-row gap-3 items-center">
        <div class="flex-grow">
          <GTextInput
            v-model="inputModel"
            :icon="newMapPinIcon"
            placeholder="Type any city, big or small"
            :disabled="processingSuggestion"
            @input="onInput"
            @keydown.down="highlightNextSuggestion"
            @keydown.up="highlightPreviousSuggestion"
            @keydown.enter="selectHighlightedSuggestion"
          />
        </div>
        <div class="hidden lg:block">
          <GButton
            variant="primary"
            :processing="processingSuggestion"
            label="Apply"
            size="medium"
            @click="suggestionComponentRef?.selectHighlightedSuggestion()"
          />
        </div>
      </div>
      <div v-if="noResults" class="text-red-500 text-center">{{ noResults }}</div>
    </div>
    <Transition name="slide-down">
      <div v-show="showCityDropdown" class="w-full">
        <SuggestionCity
          ref="suggestionComponentRef"
          :showDefaultCities="false"
          :input="inputModel"
          @select="selectSuggestion"
          @hasResults="(val:boolean) => hasResults=val"
        />
      </div>
    </Transition>
  </DefineCityDropdown>
  <div
    class="flex flex-col justify-center items-center
    gap-3 text-lg font-semibold text-v2-green-spruce
    bg-surface-green-light rounded-v2md shadow-v2md text-center"
    :class="suggestedPlaces.length === 0 ? 'py-[17px]' : 'pt-[17px]'"
  >
    <template v-if="suggestedPlaces.length === 0">
      <GIcon :svg="everywhereIcon" size="52px"/>
      <VDropdown
        data-testid="nationwide-panel"
        :positioningDisabled="isMobile"
        placement="bottom"
        :flip="false"
        popperClass="nationwide_dropdown"
      >
        {{ suggestedForUseCopy }}
        <div
          class="underline clickable text-primary-base text-lg font-semibold pt-1"
        >
          See Locations
        </div>
        <template #popper><ReuseCityDropdown/></template>
      </VDropdown>
    </template>
    <template v-else-if="selectedSuggestion">
      <div class="text-lg font-semibold">
        <VDropdown placement="bottom" :flip="false">
          Suggested for use at {{ `${suggestedPlaces.length} ${pluralize(suggestedPlaces.length, 'place')}` }} near
          <span class="underline clickable text-primary-base text-lg font-semibold">
            {{ selectedSuggestion.city }}
          </span>
          <template #popper><ReuseCityDropdown/></template>
        </VDropdown>
      </div>
      <div class="w-full" style="height: 400px;">
        <PlaceMap :places="suggestedPlaces"/>
      </div>
    </template>
  </div>
</template>
<style lang="scss">
.v-popper__popper--no-positioning.nationwide_dropdown {
  display: flex;
  height: 100%;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 9999;
}

.v-popper__popper--no-positioning.nationwide_dropdown .v-popper__backdrop {
  background: rgba(0 0 0 / 90%);
  display: block;
}

.v-popper__popper--no-positioning.nationwide_dropdown .v-popper__wrapper {
  height: 100%;
  margin-top: 35px;
  pointer-events: auto;
  transition: transform 0.15s ease-out;
  width: 100%;
}

.v-popper__popper--no-positioning.nationwide_dropdown .v-popper__wrapper .v-popper___inner {
  height: 100%;
}

.v-popper__popper--no-positioning.v-popper__popper--hidden.nationwide_dropdown .v-popper__wrapper {
  transform: translateY(100%);
}
</style>
